import {
  intranetKnowledgeCategoryFilterResponseSchema,
  intranetKnowledgeCategoryListResponseSchema,
  intranetKnowledgeCategoryPageSchema,
  intranetKnowledgeCategorySchema,
  intranetKnowledgeHomeSchema,
  intranetKnowledgeManufacturerListResponseSchema,
  intranetKnowledgeQuickAccessListSchema,
  intranetKnowledgeSearchResponseSchema,
  intranetKnowledgeSitesResponseSchema,
  intranetLeadingCategoryAssignmentSchema,
  type IntranetKnowledgeSearchQuery,
  type ReplaceIntranetKnowledgeQuickAccessRequest,
  type AssignIntranetLeadingCategoryRequest,
  type CreateIntranetKnowledgeCategoryRequest,
  type UpdateIntranetKnowledgeCategoryRequest,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api';
import { toSearchParams } from '@/lib/format';

export const intranetKnowledgeCategoriesKey = ['intranet', 'knowledge-categories'] as const;

export function intranetKnowledgeCategoryKey(slug: string | undefined) {
  return [...intranetKnowledgeCategoriesKey, 'page', slug] as const;
}

export function useUploadIntranetKnowledgeCategoryBanner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => {
      const body = new FormData();
      body.append('file', file);
      return apiFetch(
        `/intranet/knowledge-categories/${id}/banner`,
        intranetKnowledgeCategorySchema,
        { method: 'POST', body },
      );
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: intranetKnowledgeCategoriesKey }),
  });
}

export function useDeleteIntranetKnowledgeCategoryBanner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/intranet/knowledge-categories/${id}/banner`, intranetKnowledgeCategorySchema, {
        method: 'DELETE',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: intranetKnowledgeCategoriesKey }),
  });
}

export function useIntranetKnowledgeCategories(enabled = true) {
  return useQuery({
    queryKey: intranetKnowledgeCategoriesKey,
    enabled,
    queryFn: () =>
      apiFetch('/intranet/knowledge-categories', intranetKnowledgeCategoryListResponseSchema),
  });
}

export function useIntranetKnowledgeCategory(
  slug: string | undefined,
  filter?: { siteId?: string; kind?: string; manufacturerId?: string },
) {
  const siteId = filter?.siteId ?? undefined;
  const kind = filter?.kind ?? undefined;
  const manufacturerId = filter?.manufacturerId ?? undefined;
  return useQuery({
    queryKey: [
      ...intranetKnowledgeCategoryKey(slug),
      siteId ?? '',
      kind ?? '',
      manufacturerId ?? '',
    ],
    enabled: Boolean(slug),
    queryFn: () =>
      apiFetch(
        `/intranet/knowledge-categories/slug/${encodeURIComponent(slug ?? '')}${toSearchParams({
          siteId,
          kind,
          manufacturerId,
        })}`,
        intranetKnowledgeCategoryPageSchema,
      ),
  });
}

export function useIntranetKnowledgeCategoryFilter() {
  return useQuery({
    queryKey: [...intranetKnowledgeCategoriesKey, 'filter'] as const,
    queryFn: () =>
      apiFetch(
        '/intranet/knowledge-categories/filter',
        intranetKnowledgeCategoryFilterResponseSchema,
      ),
  });
}

export function useIntranetKnowledgeHome(siteId?: string) {
  const chosen = siteId ?? undefined;
  return useQuery({
    queryKey: [...intranetKnowledgeCategoriesKey, 'home', chosen ?? ''] as const,
    queryFn: () =>
      apiFetch(
        `/intranet/knowledge-home${toSearchParams({ siteId: chosen })}`,
        intranetKnowledgeHomeSchema,
      ),
  });
}

export function useIntranetKnowledgeSearch(query: IntranetKnowledgeSearchQuery) {
  const text = query.q.trim();
  const narrowing = [query.kind, query.categoryId, query.manufacturerId].some((value) =>
    Boolean(value),
  );
  return useQuery({
    queryKey: [...intranetKnowledgeCategoriesKey, 'search', query] as const,
    enabled: text.length >= 2 || narrowing,
    queryFn: () =>
      apiFetch(
        `/intranet/knowledge-search${toSearchParams({
          q: text,
          siteId: query.siteId,
          categoryId: query.categoryId,
          kind: query.kind,
          manufacturerId: query.manufacturerId,
        })}`,
        intranetKnowledgeSearchResponseSchema,
      ),
  });
}

export function useIntranetKnowledgeManufacturersReader() {
  return useQuery({
    queryKey: [...intranetKnowledgeCategoriesKey, 'manufacturers', 'filter'] as const,
    queryFn: () =>
      apiFetch(
        '/intranet/knowledge-manufacturers/filter',
        intranetKnowledgeManufacturerListResponseSchema,
      ),
  });
}

export function useIntranetKnowledgeQuickAccess(enabled = true) {
  return useQuery({
    queryKey: [...intranetKnowledgeCategoriesKey, 'quick-access'] as const,
    enabled,
    queryFn: () =>
      apiFetch('/intranet/knowledge-quick-access', intranetKnowledgeQuickAccessListSchema),
  });
}

export function useReplaceIntranetKnowledgeQuickAccess() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ReplaceIntranetKnowledgeQuickAccessRequest) =>
      apiFetch('/intranet/knowledge-quick-access', intranetKnowledgeQuickAccessListSchema, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: intranetKnowledgeCategoriesKey }),
  });
}

export function useIntranetKnowledgeSites() {
  return useQuery({
    queryKey: [...intranetKnowledgeCategoriesKey, 'sites'] as const,
    queryFn: () => apiFetch('/intranet/knowledge-sites', intranetKnowledgeSitesResponseSchema),
  });
}

export function useCreateIntranetKnowledgeCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateIntranetKnowledgeCategoryRequest) =>
      apiFetch('/intranet/knowledge-categories', intranetKnowledgeCategorySchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: intranetKnowledgeCategoriesKey }),
  });
}

export function useUpdateIntranetKnowledgeCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateIntranetKnowledgeCategoryRequest }) =>
      apiFetch(
        `/intranet/knowledge-categories/${encodeURIComponent(id)}`,
        intranetKnowledgeCategorySchema,
        { method: 'PATCH', body: JSON.stringify(body) },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: intranetKnowledgeCategoriesKey }),
  });
}

export function useAssignIntranetLeadingCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, body }: { slug: string; body: AssignIntranetLeadingCategoryRequest }) =>
      apiFetch(
        `/intranet/knowledge-categories/pages/${encodeURIComponent(slug)}`,
        intranetLeadingCategoryAssignmentSchema,
        { method: 'PATCH', body: JSON.stringify(body) },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: intranetKnowledgeCategoriesKey }),
  });
}
